import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import { pollStatus, type StatusResult } from "@/features/identity-hub/api/identity-hub.api";
import { getLocalUserFacingApiMessage, ApiError, passThroughMessage } from "@/lib/api-client";
import { isPollBudgetElapsedError } from "@/lib/poll-budget-elapsed";
import { Sentry } from "@/lib/sentry";

/**
 * Shared hook for the Amber Path async pattern:
 * POST → get correlationId → poll `/status/:id` until terminal → invalidate + toast.
 *
 * @example
 * ```ts
 * const register = useAmberMutation({
 *   mutationFn: registerUser,
 *   pollBasePath: "/users",
 *   successMessage: "User registered",
 *   invalidateKeys: [identityHubKeys.all],
 * });
 * register.mutate({ email: "...", displayName: "..." });
 * ```
 */
export interface UseAmberMutationOptions<TInput> {
  /** Async function that POSTs and returns `{ correlationId }`. */
  mutationFn: (data: TInput) => Promise<{ correlationId: string }>;
  /** Base path for status polling (e.g. `/users`). */
  pollBasePath: string;
  /** Toast message on success — string, or a function receiving the mutation variables and the polled status result for dynamic messages (e.g. a count returned by `/status/:id`). Omit to skip the success toast entirely (e.g. when the caller drives its own UX). */
  successMessage?: string | ((variables: TInput, result: StatusResult) => string);
  /** Query keys to invalidate after success. */
  invalidateKeys?: readonly QueryKey[];
  /** Optional callback after successful mutation + poll. */
  onSuccess?: (result: StatusResult, variables: TInput) => void;
  /** Optional callback when the POST or poll fails (before the error toast). */
  onMutationError?: (error: unknown, variables: TInput) => void;
  /** Fires after the POST returns a correlation id, before status polling. */
  onAccepted?: (variables: TInput) => void;
  /** When true, skip the generic error toast (caller owns the message). */
  skipErrorToast?: (error: unknown) => boolean;
  /**
   * Shown when `amberFailureToastMessage` is null (typically 403). 401 stays
   * silent so the session handler owns that UX.
   */
  fallbackErrorMessage?: string;
  /**
   * When set, owns error-toast copy (including 401 silence). Used by operator
   * screens that must not collapse 403/400 into the generic customer fallback.
   */
  resolveErrorMessage?: (err: unknown) => string | null;
  /** Runs after invalidate, before the success toast — awaited so post-success side-effects (e.g. JWT refresh) finish before the user sees success. Errors are swallowed. */
  beforeToast?: (result: StatusResult, variables: TInput) => void | Promise<void>;
  /** Max poll attempts (default 20). Ignored when `pollBudgetMs` is set. */
  maxAttempts?: number;
  /**
   * Wall-clock poll budget in ms. Polls until a terminal status or this budget,
   * instead of stopping after `maxAttempts`.
   */
  pollBudgetMs?: number;
  /** Initial poll interval in ms (default 500). */
  intervalMs?: number;
  /** Abort the status poll for this correlation id, including the wait between attempts. */
  bindPollAbort?: (correlationId: string) => AbortSignal;
  /** Intermediate status polls. Omitted callers still wait for a terminal status only. */
  onStatus?: (status: StatusResult) => void;
  releasePollAbort?: (correlationId: string) => void;
  /**
   * When true, a worker FAILED poll message may pass the leak gate in the error toast.
   * Customer-facing callers must leave this false (default).
   */
  passThroughWorkerFailure?: boolean;
  /**
   * When true, the mutation settles after the POST accepts; status polling runs in the
   * background so another mutation can start while earlier jobs are still running.
   */
  detachStatusPoll?: boolean;
}

type AmberMutationAcceptedOnly = {
  status: "ACCEPTED";
  detachStatusPoll: true;
};

function isAcceptedOnlyResult(result: StatusResult | AmberMutationAcceptedOnly): result is AmberMutationAcceptedOnly {
  return "detachStatusPoll" in result && result.detachStatusPoll === true;
}

/** Worker FAILED poll result with a non-empty human message — the only plain Error trusted for pass-through. */
export class AmberWorkerFailureError extends Error {
  override readonly name = "AmberWorkerFailureError";
  /** Domain code from the poll payload (`result.error`) and, for field-level failures, the field it rejected. */
  readonly code: string | undefined;
  readonly field: string | undefined;

  constructor(message: string, result: { error?: string; field?: unknown } = {}) {
    super(message);
    this.code = result.error;
    this.field = typeof result.field === "string" ? result.field : undefined;
  }
}

/** Status JSON is untyped. Only a string can be trimmed. */
export function readTrimmedStatusField(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function amberFailureToastMessage(
  err: unknown,
  passThroughWorkerFailure = false,
): string | null {
  if (passThroughWorkerFailure && err instanceof AmberWorkerFailureError) {
    return getLocalUserFacingApiMessage(
      new ApiError(400, err.message),
      passThroughMessage,
    );
  }
  return getLocalUserFacingApiMessage(err);
}

/** Error toast copy for Amber mutations, including an optional 403 fallback. */
export function resolveAmberErrorToastMessage(
  err: unknown,
  options: {
    passThroughWorkerFailure?: boolean;
    fallbackErrorMessage?: string;
  } = {},
): string | null {
  const message = amberFailureToastMessage(err, options.passThroughWorkerFailure ?? false);
  if (message) return message;
  if (!options.fallbackErrorMessage) return null;
  if (err instanceof ApiError && err.status === 401) return null;
  return options.fallbackErrorMessage;
}

export function useAmberMutation<TInput>(
  options: UseAmberMutationOptions<TInput>,
) {
  const {
    mutationFn,
    pollBasePath,
    successMessage,
    invalidateKeys,
    onSuccess,
    onMutationError,
    onAccepted,
    skipErrorToast,
    fallbackErrorMessage,
    resolveErrorMessage,
    beforeToast,
    maxAttempts,
    pollBudgetMs,
    intervalMs,
    bindPollAbort,
    releasePollAbort,
    onStatus,
    passThroughWorkerFailure = false,
    detachStatusPoll = false,
  } = options;

  const qc = useQueryClient();

  const handleMutationError = (err: unknown, variables: TInput) => {
    if (err instanceof Error && err.name === "AbortError") return;
    // The claim window ended while the job was still non-terminal. Not a failure.
    if (isPollBudgetElapsedError(err)) {
      onMutationError?.(err, variables);
      return;
    }
    Sentry.captureException(err);
    onMutationError?.(err, variables);
    if (skipErrorToast?.(err)) return;
    const message = resolveErrorMessage
      ? resolveErrorMessage(err)
      : resolveAmberErrorToastMessage(err, {
          passThroughWorkerFailure,
          fallbackErrorMessage,
        });
    if (message) toast.error(message);
  };

  const handlePollSuccess = async (result: StatusResult, variables: TInput) => {
    if (invalidateKeys) {
      await Promise.all(
        invalidateKeys.map((key) => qc.invalidateQueries({ queryKey: key })),
      );
    }
    if (beforeToast) {
      try {
        await beforeToast(result, variables);
      } catch {
        /* non-fatal — toast still fires */
      }
    }
    if (successMessage) {
      toast.success(
        typeof successMessage === "function" ? successMessage(variables, result) : successMessage,
      );
    }
    onSuccess?.(result, variables);
  };

  const pollUntilTerminal = async (
    correlationId: string,
    data: TInput,
  ): Promise<StatusResult> => {
    const signal = bindPollAbort?.(correlationId);
    try {
      const result = await pollStatus(
        pollBasePath,
        correlationId,
        maxAttempts,
        intervalMs,
        signal,
        pollBudgetMs,
        onStatus,
      );
      if (result.status === "FAILED") {
        const workerMessage = readTrimmedStatusField(result.message);
        const workerCode = readTrimmedStatusField(result.error);
        if (workerMessage) {
          throw new AmberWorkerFailureError(workerMessage, {
            error: workerCode || undefined,
            field: result.field,
          });
        }
        throw new Error(workerCode ?? "Operation failed");
      }
      return result;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        return { status: "COMPLETED" as const, cancelled: true, clientAborted: true };
      }
      throw err;
    } finally {
      releasePollAbort?.(correlationId);
    }
  };

  const runDetachedPoll = (correlationId: string, data: TInput) => {
    void (async () => {
      try {
        const result = await pollUntilTerminal(correlationId, data);
        await handlePollSuccess(result, data);
      } catch (err) {
        handleMutationError(err, data);
      }
    })();
  };

  return useMutation({
    mutationFn: async (data: TInput) => {
      const { correlationId } = await mutationFn(data);
      onAccepted?.(data);
      if (detachStatusPoll) {
        runDetachedPoll(correlationId, data);
        return { status: "ACCEPTED", detachStatusPoll: true } satisfies AmberMutationAcceptedOnly;
      }
      return pollUntilTerminal(correlationId, data);
    },
    onSuccess: async (result, variables) => {
      if (isAcceptedOnlyResult(result)) return;
      await handlePollSuccess(result, variables);
    },
    onError: (err, variables) => {
      handleMutationError(err, variables);
    },
    meta: { skipGlobalError: true },
  });
}
